import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  CalendarDays,
  CreditCard,
  DollarSign,
  HelpCircle,
  LayoutDashboard,
  Percent,
  RefreshCw,
  ShoppingCart,
  Star,
  Target,
  TrendingUp,
  AlertTriangle,
  UserPlus,
} from "lucide-react";
import {
  BuyAllPurchase,
  BuyAllPurchasesSummary,
  BuyAdminSubscription,
  BuyAdminUserAdminSummary,
  BuyAnalyticsKeyMetrics,
  BuyAnalyticsTopEvent,
  BuyOrderFinancialsSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import {
  MEMBER_SAVINGS_LABEL,
  NON_PURCHASING_MEMBERS_LABEL,
  PURCHASING_MEMBERS_LABEL,
  REVENUE_LABEL,
  TICKETS_SOLD_LABEL,
  timeframeHint,
} from "../shared/buyAdminMetricLabels";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./Dashboard.module.css";

const emptyPurchaseSummary: BuyAllPurchasesSummary = {
  total_purchases: 0,
  total_revenue: 0,
  member_savings: 0,
  fulfillment_rate: 0,
};

const emptyFinancialSummary: BuyOrderFinancialsSummary = {
  order_count: 0,
  ticket_quantity: 0,
  customer_subtotal: 0,
  customer_tax: 0,
  customer_total: 0,
  refunded_amount: 0,
  net_customer_collected: 0,
  te_ticket_cost: 0,
  te_tax_total: 0,
  te_service_fee_total: 0,
  qa_pay_to_te_total: 0,
  stripe_processing_fee_total: 0,
  gross_spread: 0,
  net_after_stripe: 0,
  refund_adjusted_net_after_stripe: 0,
  gap_order_count: 0,
};

const emptyAnalyticsKeyMetrics: BuyAnalyticsKeyMetrics = {
  active_members: 0,
  revenue: 0,
  tickets_sold: 0,
  member_savings: 0,
  new_signups: 0,
  changes: {
    active_members: 0,
    revenue: 0,
    tickets_sold: 0,
    member_savings: 0,
    new_signups: 0,
  },
};

const PLACEHOLDER = "—";

const ANALYTICS_TIMEFRAME_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "12m", label: "Last 12 months" },
] as const;

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const formatNumber = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US");

const formatPercent = (value: number | null | undefined) =>
  `${Number(value || 0).toFixed(1)}%`;

const displayNumber = (isLoading: boolean, value: number | null | undefined) =>
  isLoading ? PLACEHOLDER : formatNumber(value);

const displayCurrency = (isLoading: boolean, value: number | null | undefined) =>
  isLoading ? PLACEHOLDER : formatCurrency(value);

const displayPercent = (isLoading: boolean, value: number | null | undefined) =>
  isLoading ? PLACEHOLDER : formatPercent(value);

const displayText = (isLoading: boolean, value: string) =>
  isLoading ? PLACEHOLDER : value;

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const firstFiniteNumber = (...values: Array<number | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
};

const resolveAnnualMembershipFee = (subscriptions: BuyAdminSubscription[]): number | null => {
  const activePlans = subscriptions.filter((plan) => plan.is_active && plan.amount_cents > 0);
  const annualPlan = activePlans.find((plan) => plan.subscription_type?.toLowerCase() === "annual");
  if (annualPlan) {
    return annualPlan.amount_cents / 100;
  }

  const monthlyPlan = activePlans.find((plan) => plan.subscription_type?.toLowerCase() === "monthly");
  if (monthlyPlan) {
    return (monthlyPlan.amount_cents / 100) * 12;
  }

  return null;
};

const tabs = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "membership", label: "Membership Health", icon: Star },
  { key: "subscription", label: "Subscription Revenue", icon: CreditCard },
  { key: "marketplace", label: "Ticket Purchases", icon: ShoppingCart },
  { key: "growth", label: "Growth & Acquisition", icon: TrendingUp },
  { key: "behavior", label: "User Behavior", icon: Activity },
  { key: "operations", label: "Operations", icon: AlertTriangle },
];

type BuyMetricTone = "blue" | "green" | "purple" | "amber" | "red" | "slate";

const resolveMetricTone = (Icon: React.ElementType): BuyMetricTone => {
  if (Icon === DollarSign || Icon === TrendingUp) return "green";
  if (Icon === CreditCard || Icon === Target) return "purple";
  if (Icon === AlertTriangle) return "red";
  if (Icon === Percent || Icon === Star) return "amber";
  if (Icon === ShoppingCart || Icon === Activity || Icon === UserPlus || Icon === CalendarDays) return "blue";
  return "slate";
};

const BuyAdminDashboard = () => {
  const [purchaseSummary, setPurchaseSummary] = useState<BuyAllPurchasesSummary>(emptyPurchaseSummary);
  const [financialSummary, setFinancialSummary] = useState<BuyOrderFinancialsSummary>(emptyFinancialSummary);
  const [memberSummary, setMemberSummary] = useState<BuyAdminUserAdminSummary>({});
  const [affiliateSummary, setAffiliateSummary] = useState<BuyAdminUserAdminSummary>({});
  const [recentPurchases, setRecentPurchases] = useState<BuyAllPurchase[]>([]);
  const [topEvents, setTopEvents] = useState<BuyAnalyticsTopEvent[]>([]);
  const [analyticsKeyMetrics, setAnalyticsKeyMetrics] = useState<BuyAnalyticsKeyMetrics>(emptyAnalyticsKeyMetrics);
  const [subscriptions, setSubscriptions] = useState<BuyAdminSubscription[]>([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [analyticsTimeframe, setAnalyticsTimeframe] = useState("30d");
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const filterRef = useRef<HTMLDivElement>(null);
  const skipTimeframeFetchRef = useRef(true);

  const dashboardPeriodHint = timeframeHint(analyticsTimeframe);
  const metricsLoading = loading || analyticsLoading;

  const applyAnalyticsResponse = (analyticsResponse: Awaited<ReturnType<typeof buyAdminApi.getBuyAnalytics>>) => {
    setTopEvents((analyticsResponse.top_events || []).slice(0, 5));
    setAnalyticsKeyMetrics(analyticsResponse.key_metrics || emptyAnalyticsKeyMetrics);
  };

  const loadAnalytics = async (timeframe: string) => {
    setAnalyticsLoading(true);
    setErrorMessage("");

    try {
      const [analyticsResponse, membersResponse] = await Promise.all([
        buyAdminApi.getBuyAnalytics({ timeframe }),
        buyAdminApi.getUserAdminCustomers({
          limit: 1,
          offset: 0,
          timeframe,
        }),
      ]);
      applyAnalyticsResponse(analyticsResponse);
      setMemberSummary((prev) => ({
        ...prev,
        ...(membersResponse.summary || {}),
      }));
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load dashboard analytics.");
      setTopEvents([]);
      setAnalyticsKeyMetrics(emptyAnalyticsKeyMetrics);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const [
        purchasesResponse,
        membersResponse,
        affiliatesResponse,
        analyticsResponse,
        financialsResponse,
        subscriptionsResponse,
      ] = await Promise.all([
        buyAdminApi.getAllPurchases({
          page: 1,
          limit: 6,
          sort_by: "date",
          sort_dir: "desc",
        }),
        buyAdminApi.getUserAdminCustomers({
          limit: 1,
          offset: 0,
          timeframe: analyticsTimeframe,
        }),
        buyAdminApi.getUserAdminAffiliates({
          limit: 1,
          offset: 0,
        }),
        buyAdminApi.getBuyAnalytics({
          timeframe: analyticsTimeframe,
        }),
        buyAdminApi.getOrderFinancials({
          limit: 1,
          offset: 0,
          completedOnly: "false",
        }),
        buyAdminApi.getSubscriptions(),
      ]);

      setPurchaseSummary(purchasesResponse.summary || emptyPurchaseSummary);
      setRecentPurchases(purchasesResponse.data || []);
      setMemberSummary(membersResponse.summary || {});
      setAffiliateSummary(affiliatesResponse.summary || {});
      applyAnalyticsResponse(analyticsResponse);
      setFinancialSummary(financialsResponse.summary || emptyFinancialSummary);
      setSubscriptions(subscriptionsResponse.data || []);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load Buy Admin dashboard.");
      setPurchaseSummary(emptyPurchaseSummary);
      setFinancialSummary(emptyFinancialSummary);
      setMemberSummary({});
      setAffiliateSummary({});
      setRecentPurchases([]);
      setTopEvents([]);
      setAnalyticsKeyMetrics(emptyAnalyticsKeyMetrics);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (skipTimeframeFetchRef.current) {
      skipTimeframeFetchRef.current = false;
      return;
    }
    loadAnalytics(analyticsTimeframe);
  }, [analyticsTimeframe]);

  useEffect(() => {
    if (!filterOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterOpen]);

  const handleTimeframeChange = (timeframe: string) => {
    setAnalyticsTimeframe(timeframe);
    setFilterOpen(false);
  };

  const purchasingMembers30d = Number(analyticsKeyMetrics.active_members ?? 0);
  const savings30d = Number(analyticsKeyMetrics.member_savings ?? 0);
  const ticketsSold30d = Number(analyticsKeyMetrics.tickets_sold ?? 0);
  const revenue30d = Number(analyticsKeyMetrics.revenue ?? 0);
  const newSignups = useMemo(() => {
    if (typeof memberSummary.new_signups === "number" && Number.isFinite(memberSummary.new_signups)) {
      return memberSummary.new_signups;
    }
    if (typeof analyticsKeyMetrics.new_signups === "number" && Number.isFinite(analyticsKeyMetrics.new_signups)) {
      return analyticsKeyMetrics.new_signups;
    }
    return 0;
  }, [memberSummary.new_signups, analyticsKeyMetrics.new_signups]);
  const totalMembers = Number(memberSummary.customer_count ?? 0);
  const premiumMembers = Number(memberSummary.premium_count ?? 0);
  const accountActiveMembers = Number(memberSummary.active_count ?? 0);
  const nonPurchasingMembers30d = Math.max(0, totalMembers - purchasingMembers30d);
  const deactivatedMembers = Math.max(0, totalMembers - accountActiveMembers);
  const totalTickets = Number(financialSummary.ticket_quantity ?? 0);
  const avgTicketsPerMember = totalMembers ? ticketsSold30d / totalMembers : 0;
  const customerTotal = firstFiniteNumber(financialSummary.customer_total, purchaseSummary.total_revenue);
  const totalPurchases = firstFiniteNumber(purchaseSummary.total_purchases, financialSummary.order_count);
  const annualMembershipFee = useMemo(
    () => resolveAnnualMembershipFee(subscriptions),
    [subscriptions],
  );
  const avgSavingsPerTicket = ticketsSold30d ? savings30d / ticketsSold30d : 0;
  const breakevenTickets = useMemo(() => {
    if (annualMembershipFee == null || avgSavingsPerTicket <= 0) {
      return 0;
    }
    return Math.ceil(annualMembershipFee / avgSavingsPerTicket);
  }, [annualMembershipFee, avgSavingsPerTicket]);
  const breakevenPercent = breakevenTickets ? Math.min(100, (avgTicketsPerMember / breakevenTickets) * 100) : 0;
  const avgOrderValue = totalPurchases ? customerTotal / totalPurchases : 0;
  const avgTicketPrice = totalTickets ? Number(financialSummary.customer_total ?? 0) / totalTickets : 0;
  const premiumRate = totalMembers ? (premiumMembers / totalMembers) * 100 : 0;
  const purchaseRate30d = useMemo(() => {
    if (!totalMembers) return 0;
    return (purchasingMembers30d / totalMembers) * 100;
  }, [purchasingMembers30d, totalMembers]);
  const deactivatedRate = useMemo(() => {
    if (!totalMembers) return 0;
    return (deactivatedMembers / totalMembers) * 100;
  }, [deactivatedMembers, totalMembers]);
  const subscriptionRevenue = useMemo(() => {
    if (annualMembershipFee == null) {
      return null;
    }
    return premiumMembers * annualMembershipFee;
  }, [annualMembershipFee, premiumMembers]);

  const netMargin = firstFiniteNumber(financialSummary.refund_adjusted_net_after_stripe, financialSummary.net_after_stripe);
  const ltvProxy = useMemo(() => {
    if (annualMembershipFee == null) {
      return "-";
    }
    return formatCurrency(
      annualMembershipFee + avgTicketsPerMember * Math.max(0, netMargin / Math.max(totalTickets, 1)),
    );
  }, [annualMembershipFee, avgTicketsPerMember, netMargin, totalTickets]);
  const refundRate = Number(financialSummary.customer_total ?? 0)
    ? (Number(financialSummary.refunded_amount ?? 0) / Number(financialSummary.customer_total ?? 0)) * 100
    : 0;

  const MetricCard = ({
    label,
    value,
    hint,
    icon: Icon = HelpCircle,
    tone,
    isLoading = false,
  }: {
    label: string;
    value: string;
    hint?: string;
    icon?: React.ElementType;
    tone?: BuyMetricTone;
    isLoading?: boolean;
  }) => {
    const cardTone = tone || resolveMetricTone(Icon);

    return (
      <div className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--${cardTone}`}>
        <div className="buy-admin-stat-head">
          <div className="buy-admin-stat-icon">
            <Icon size={18} />
          </div>
          <div className="buy-admin-stat-copy">
            <span className="buy-admin-stat-label">{label}</span>
          </div>
        </div>
        <div>
          <strong className={isLoading ? styles.metricLoading : ""}>{value}</strong>
          {hint && <div className={styles.summaryHint}>{hint}</div>}
        </div>
      </div>
    );
  };

  const HealthGrid = ({
    items,
    isLoading = false,
  }: {
    items: Array<{ label: string; value: string }>;
    isLoading?: boolean;
  }) => (
    <div className={styles.healthGrid}>
      {items.map((item) => (
        <div className={styles.healthItem} key={item.label}>
          <span>{item.label}</span>
          <strong className={isLoading ? styles.metricLoading : ""}>{item.value}</strong>
        </div>
      ))}
    </div>
  );

  const RecentPurchasesPanel = () => (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2>Recent Purchases</h2>
        <span>Latest orders</span>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Member</th>
              <th>Event</th>
              <th>Date</th>
              <th>Status</th>
              <th className={styles.right}>Total</th>
            </tr>
          </thead>
          <tbody>
            {recentPurchases.map((purchase) => (
              <tr key={purchase.id}>
                <td>{purchase.id}</td>
                <td>
                  {purchase.member}
                  <span>{purchase.email || "-"}</span>
                </td>
                <td>{purchase.event}</td>
                <td>{formatDate(purchase.date)}</td>
                <td>{purchase.status || "-"}</td>
                <td className={styles.right}>{formatCurrency(purchase.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && recentPurchases.length === 0 && (
          <div className={styles.emptyState}>No recent purchases found.</div>
        )}
      </div>
    </section>
  );

  const TopEventsPanel = () => (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2>Top Events</h2>
        <span>{dashboardPeriodHint}</span>
      </div>
      <div className={styles.topEvents}>
        {topEvents.map((event) => (
          <div className={styles.eventRow} key={event.event}>
            <div>
              <strong>{event.event}</strong>
              <span>
                {formatNumber(event.tickets_sold)} tickets - {formatCurrency(event.member_savings)} saved
              </span>
            </div>
            <div className={styles.right}>{formatCurrency(event.revenue)}</div>
          </div>
        ))}
        {!metricsLoading && topEvents.length === 0 && (
          <div className={styles.emptyState}>No top event data found.</div>
        )}
        {metricsLoading && topEvents.length === 0 && (
          <div className={styles.emptyState}>Loading top events…</div>
        )}
      </div>
    </section>
  );

  const BreakevenPanel = () => (
    <section className={styles.breakevenPanel}>
      <div className={styles.breakevenTitle}>
        <Target size={22} color="#10b981" />
        <h2>Breakeven Analysis</h2>
        <span className={styles.achievedBadge}>
          {metricsLoading
            ? PLACEHOLDER
            : avgTicketsPerMember >= breakevenTickets && breakevenTickets
              ? "Achieved"
              : "Below Target"}
        </span>
      </div>
      <div className={styles.breakevenGrid}>
        <div className={styles.breakevenCard}>
          <div>
            <strong className={loading ? styles.metricLoading : ""}>
              {loading ? PLACEHOLDER : breakevenTickets || "-"}
            </strong>
            <span>Breakeven Tickets</span>
          </div>
        </div>
        <div className={`${styles.breakevenCard} ${styles.breakevenGreen}`}>
          <div>
            <strong className={metricsLoading ? styles.metricLoading : ""}>
              {metricsLoading ? PLACEHOLDER : avgTicketsPerMember ? avgTicketsPerMember.toFixed(1) : "-"}
            </strong>
            <span>Avg Tickets/Member</span>
          </div>
        </div>
        <div className={`${styles.breakevenCard} ${styles.breakevenBlue}`}>
          <div>
            <strong className={metricsLoading ? styles.metricLoading : ""}>
              {displayCurrency(metricsLoading, avgSavingsPerTicket)}
            </strong>
            <span>Avg Savings/Ticket</span>
          </div>
        </div>
        <div className={`${styles.breakevenCard} ${styles.breakevenPurple}`}>
          <div>
            <strong className={metricsLoading ? styles.metricLoading : ""}>
              {displayCurrency(metricsLoading, savings30d)}
            </strong>
            <span>{MEMBER_SAVINGS_LABEL} ({dashboardPeriodHint})</span>
          </div>
        </div>
      </div>
      <div className={styles.progressBlock}>
        <div className={styles.progressHeader}>
          <span>Member Value Progress</span>
          <span>
            {metricsLoading
              ? PLACEHOLDER
              : breakevenTickets
                ? `${Math.round(breakevenPercent)}% of breakeven`
                : "No breakeven target"}
          </span>
        </div>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: metricsLoading ? "0%" : `${breakevenPercent}%` }}
          />
        </div>
      </div>
    </section>
  );

  const renderOverview = () => (
    <>
      <div className={styles.summaryGrid}>
        <MetricCard
          label={PURCHASING_MEMBERS_LABEL}
          value={displayNumber(metricsLoading, purchasingMembers30d)}
          hint={dashboardPeriodHint}
          icon={Star}
          isLoading={metricsLoading}
        />
        <MetricCard
          label="ARR"
          value={
            loading
              ? PLACEHOLDER
              : subscriptionRevenue != null
                ? formatCurrency(subscriptionRevenue)
                : "-"
          }
          hint={annualMembershipFee != null ? "Premium members × annual plan fee" : "No active subscription plan configured"}
          icon={DollarSign}
          isLoading={loading}
        />
        <MetricCard
          label="Premium Members"
          value={displayNumber(loading, premiumMembers)}
          hint="Current premium count"
          icon={TrendingUp}
          isLoading={loading}
        />
        <MetricCard
          label={NON_PURCHASING_MEMBERS_LABEL}
          value={displayNumber(metricsLoading, nonPurchasingMembers30d)}
          hint={dashboardPeriodHint}
          icon={Percent}
          isLoading={metricsLoading}
        />
        <MetricCard
          label={TICKETS_SOLD_LABEL}
          value={displayNumber(metricsLoading, ticketsSold30d)}
          hint={dashboardPeriodHint}
          icon={ShoppingCart}
          isLoading={metricsLoading}
        />
        <MetricCard
          label={MEMBER_SAVINGS_LABEL}
          value={displayCurrency(metricsLoading, savings30d)}
          hint={dashboardPeriodHint}
          icon={CreditCard}
          isLoading={metricsLoading}
        />
      </div>
      <BreakevenPanel />
      <div className={styles.sectionGrid}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2>Member Health</h2>
            <span>{dashboardPeriodHint} purchase activity</span>
          </div>
          <HealthGrid
            isLoading={loading}
            items={[
              { label: "Total Members", value: displayNumber(loading, totalMembers) },
              { label: "Premium Members", value: displayNumber(loading, premiumMembers) },
              { label: PURCHASING_MEMBERS_LABEL, value: displayNumber(metricsLoading, purchasingMembers30d) },
              { label: NON_PURCHASING_MEMBERS_LABEL, value: displayNumber(metricsLoading, nonPurchasingMembers30d) },
              { label: "Purchase Rate", value: displayPercent(metricsLoading, purchaseRate30d) },
              { label: MEMBER_SAVINGS_LABEL, value: displayCurrency(metricsLoading, savings30d) },
            ]}
          />
        </section>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2>Financial Snapshot</h2>
            <span>Order economics</span>
          </div>
          <HealthGrid
            isLoading={loading}
            items={[
              { label: `${REVENUE_LABEL} (${dashboardPeriodHint})`, value: displayCurrency(metricsLoading, revenue30d) },
              { label: "Customer Collected (All Time)", value: displayCurrency(loading, financialSummary.customer_total) },
              { label: "Net Margin", value: displayCurrency(loading, netMargin) },
              { label: `${TICKETS_SOLD_LABEL} (${dashboardPeriodHint})`, value: displayNumber(metricsLoading, ticketsSold30d) },
            ]}
          />
        </section>
      </div>
      <div className={styles.sectionGrid}>
        <RecentPurchasesPanel />
        <TopEventsPanel />
      </div>
    </>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "membership":
        return (
          <>
            <div className={styles.summaryGrid}>
              <MetricCard label="Total Members" value={displayNumber(loading, totalMembers)} icon={Star} isLoading={loading} />
              <MetricCard
                label={PURCHASING_MEMBERS_LABEL}
                value={displayNumber(metricsLoading, purchasingMembers30d)}
                hint={dashboardPeriodHint}
                icon={Activity}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Premium"
                value={displayNumber(loading, premiumMembers)}
                hint={displayPercent(loading, premiumRate)}
                icon={CreditCard}
                isLoading={loading}
              />
              <MetricCard
                label={NON_PURCHASING_MEMBERS_LABEL}
                value={displayNumber(metricsLoading, nonPurchasingMembers30d)}
                hint={dashboardPeriodHint}
                icon={AlertTriangle}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Deactivated Accounts"
                value={displayNumber(loading, deactivatedMembers)}
                hint={displayPercent(loading, deactivatedRate)}
                icon={TrendingUp}
                isLoading={loading}
              />
              <MetricCard
                label="Affiliates"
                value={displayNumber(loading, affiliateSummary.affiliate_count)}
                icon={Star}
                isLoading={loading}
              />
            </div>
            <div className={styles.sectionGrid}>
              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <h2>Membership Mix</h2>
                  <span>Activation and premium mix</span>
                </div>
                <HealthGrid
                  items={[
                    { label: "Purchase Rate", value: displayPercent(metricsLoading, purchaseRate30d) },
                    { label: "Premium Rate", value: displayPercent(loading, premiumRate) },
                    { label: NON_PURCHASING_MEMBERS_LABEL, value: displayNumber(metricsLoading, nonPurchasingMembers30d) },
                    {
                      label: `${MEMBER_SAVINGS_LABEL} per member`,
                      value: displayCurrency(
                        metricsLoading,
                        purchasingMembers30d ? savings30d / purchasingMembers30d : 0,
                      ),
                    },
                    { label: "Deactivated Accounts", value: displayNumber(loading, deactivatedMembers) },
                    { label: `${TICKETS_SOLD_LABEL} (${dashboardPeriodHint})`, value: displayNumber(metricsLoading, ticketsSold30d) },
                  ]}
                />
              </section>
              <RecentPurchasesPanel />
            </div>
          </>
        );
      case "subscription":
        return (
          <>
            <div className={styles.summaryGrid}>
              <MetricCard
                label="Gross Subscription Revenue"
                value={
                  loading
                    ? PLACEHOLDER
                    : subscriptionRevenue != null
                      ? formatCurrency(subscriptionRevenue)
                      : "-"
                }
                icon={DollarSign}
                isLoading={loading}
              />
              <MetricCard
                label="ARR"
                value={
                  loading
                    ? PLACEHOLDER
                    : subscriptionRevenue != null
                      ? formatCurrency(subscriptionRevenue)
                      : "-"
                }
                icon={CreditCard}
                isLoading={loading}
              />
              <MetricCard
                label="ARPM"
                value={
                  loading
                    ? PLACEHOLDER
                    : subscriptionRevenue != null && totalMembers
                      ? formatCurrency(subscriptionRevenue / totalMembers)
                      : "-"
                }
                icon={Star}
                isLoading={loading}
              />
              <MetricCard label="Renewal Base" value={displayNumber(loading, premiumMembers)} icon={Activity} isLoading={loading} />
              <MetricCard
                label="Deactivated Rate"
                value={displayPercent(loading, deactivatedRate)}
                hint="Account status"
                icon={AlertTriangle}
                isLoading={loading}
              />
              <MetricCard
                label="LTV Proxy"
                value={displayText(loading, ltvProxy)}
                icon={TrendingUp}
                isLoading={loading}
              />
            </div>
            <BreakevenPanel />
          </>
        );
      case "marketplace":
        return (
          <>
            <div className={styles.summaryGrid}>
              <MetricCard
                label={TICKETS_SOLD_LABEL}
                value={displayNumber(metricsLoading, ticketsSold30d)}
                hint={dashboardPeriodHint}
                icon={ShoppingCart}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Orders (All Time)"
                value={displayNumber(loading, totalPurchases)}
                icon={CreditCard}
                isLoading={loading}
              />
              <MetricCard
                label="GMV (All Time)"
                value={displayCurrency(loading, financialSummary.customer_total)}
                icon={DollarSign}
                isLoading={loading}
              />
              <MetricCard
                label="Avg Ticket Price"
                value={displayCurrency(loading, avgTicketPrice)}
                icon={Target}
                isLoading={loading}
              />
              <MetricCard
                label="Avg Order Value"
                value={displayCurrency(loading, avgOrderValue)}
                icon={Star}
                isLoading={loading}
              />
              <MetricCard
                label={MEMBER_SAVINGS_LABEL}
                value={displayCurrency(metricsLoading, savings30d)}
                hint={dashboardPeriodHint}
                icon={TrendingUp}
                isLoading={metricsLoading}
              />
            </div>
            <div className={styles.sectionGrid}>
              <TopEventsPanel />
              <RecentPurchasesPanel />
            </div>
          </>
        );
      case "growth":
        return (
          <>
            <div className={styles.summaryGrid}>
              <MetricCard
                label="New Signups"
                value={displayNumber(metricsLoading, newSignups)}
                hint={dashboardPeriodHint}
                icon={UserPlus}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Premium Members"
                value={displayNumber(loading, premiumMembers)}
                hint="Current premium count"
                icon={TrendingUp}
                isLoading={loading}
              />
              <MetricCard
                label="Purchase Rate"
                value={displayPercent(metricsLoading, purchaseRate30d)}
                hint={dashboardPeriodHint}
                icon={Percent}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Referral Network"
                value={displayNumber(loading, affiliateSummary.affiliate_count)}
                icon={Star}
                isLoading={loading}
              />
              <MetricCard
                label="Active Referrers"
                value={displayNumber(loading, affiliateSummary.active_affiliate_count)}
                icon={Activity}
                isLoading={loading}
              />
              <MetricCard
                label="CAC Payback Proxy"
                value={metricsLoading ? PLACEHOLDER : `${breakevenTickets || "-"} tickets`}
                icon={CreditCard}
                isLoading={metricsLoading}
              />
            </div>
            <section className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2>Growth Levers</h2>
                <span>{dashboardPeriodHint}</span>
              </div>
              <HealthGrid
                items={[
                  { label: "New Signups", value: displayNumber(metricsLoading, newSignups) },
                  { label: PURCHASING_MEMBERS_LABEL, value: displayNumber(metricsLoading, purchasingMembers30d) },
                  { label: NON_PURCHASING_MEMBERS_LABEL, value: displayNumber(metricsLoading, nonPurchasingMembers30d) },
                  {
                    label: "Affiliate activation",
                    value: displayPercent(
                      loading,
                      Number(affiliateSummary.affiliate_count)
                        ? (Number(affiliateSummary.active_affiliate_count || 0) / Number(affiliateSummary.affiliate_count || 1)) * 100
                        : 0,
                    ),
                  },
                  { label: MEMBER_SAVINGS_LABEL, value: displayCurrency(metricsLoading, savings30d) },
                  { label: "Average savings per ticket", value: displayCurrency(metricsLoading, avgSavingsPerTicket) },
                  { label: "Breakeven tickets", value: metricsLoading ? PLACEHOLDER : breakevenTickets ? String(breakevenTickets) : "-" },
                ]}
              />
            </section>
          </>
        );
      case "behavior":
        return (
          <>
            <div className={styles.summaryGrid}>
              <MetricCard
                label="Avg Tickets/Member"
                value={metricsLoading ? PLACEHOLDER : avgTicketsPerMember.toFixed(1)}
                icon={ShoppingCart}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Orders/Member"
                value={loading ? PLACEHOLDER : totalMembers ? (totalPurchases / totalMembers).toFixed(1) : "0.0"}
                icon={Activity}
                isLoading={loading}
              />
              <MetricCard
                label="Avg Savings/Ticket"
                value={displayCurrency(metricsLoading, avgSavingsPerTicket)}
                hint={dashboardPeriodHint}
                icon={Star}
                isLoading={metricsLoading}
              />
              <MetricCard
                label="Breakeven Tickets"
                value={loading ? PLACEHOLDER : breakevenTickets ? String(breakevenTickets) : "-"}
                icon={Target}
                isLoading={loading}
              />
              <MetricCard
                label="Members Above Value Target"
                value={displayPercent(metricsLoading, breakevenPercent)}
                icon={TrendingUp}
                isLoading={metricsLoading}
              />
              <MetricCard
                label={`${MEMBER_SAVINGS_LABEL}/Member`}
                value={displayCurrency(metricsLoading, purchasingMembers30d ? savings30d / purchasingMembers30d : 0)}
                hint={dashboardPeriodHint}
                icon={DollarSign}
                isLoading={metricsLoading}
              />
            </div>
            <div className={styles.sectionGrid}>
              <BreakevenPanel />
              <RecentPurchasesPanel />
            </div>
          </>
        );
      case "operations":
        return (
          <>
            <div className={styles.summaryGrid}>
              <MetricCard
                label="Fulfillment Rate"
                value={displayPercent(loading, purchaseSummary.fulfillment_rate)}
                icon={Activity}
                isLoading={loading}
              />
              <MetricCard
                label="Financial Gaps"
                value={displayNumber(loading, financialSummary.gap_order_count)}
                icon={AlertTriangle}
                isLoading={loading}
              />
              <MetricCard
                label="Refunded Amount"
                value={displayCurrency(loading, financialSummary.refunded_amount)}
                icon={CreditCard}
                isLoading={loading}
              />
              <MetricCard label="Refund Rate" value={displayPercent(loading, refundRate)} icon={Percent} isLoading={loading} />
              <MetricCard
                label="Net Collected"
                value={displayCurrency(loading, financialSummary.net_customer_collected)}
                icon={DollarSign}
                isLoading={loading}
              />
              <MetricCard label="Net Margin" value={displayCurrency(loading, netMargin)} icon={TrendingUp} isLoading={loading} />
            </div>
            <div className={styles.sectionGrid}>
              <RecentPurchasesPanel />
              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <h2>Operational Risk</h2>
                  <span>Fulfillment and finance watchlist</span>
                </div>
                <HealthGrid
                  items={[
                    { label: "Outstanding gap orders", value: displayNumber(loading, financialSummary.gap_order_count) },
                    { label: "Refund adjusted net", value: displayCurrency(loading, financialSummary.refund_adjusted_net_after_stripe) },
                    { label: "Stripe processing fees", value: displayCurrency(loading, financialSummary.stripe_processing_fee_total) },
                    { label: "TE ticket cost", value: displayCurrency(loading, financialSummary.te_ticket_cost) },
                    { label: "QA pay to TE", value: displayCurrency(loading, financialSummary.qa_pay_to_te_total) },
                    { label: "Gross spread", value: displayCurrency(loading, financialSummary.gross_spread) },
                  ]}
                />
              </section>
            </div>
          </>
        );
      case "overview":
      default:
        return renderOverview();
    }
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`}>
      <div className={styles.header}>
        <div>
          <div className={styles.eyebrow}>Buy Admin</div>
          <h1>Dashboard</h1>
          <p>Executive view of members, purchases, revenue, savings, and fulfillment. Purchase metrics use {dashboardPeriodHint.toLowerCase()}.</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            className={styles.refreshButton}
            type="button"
            onClick={loadDashboard}
            disabled={loading || analyticsLoading}
            aria-label="Refresh dashboard"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading || analyticsLoading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      {errorMessage && <div className={styles.errorState}>{errorMessage}</div>}

      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            className={`${styles.tabButton} ${activeTab === tab.key ? styles.tabActive : ""}`}
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.sectionHeader}>
        <h2>{activeTab === "overview" ? "Executive Overview" : tabs.find((tab) => tab.key === activeTab)?.label}</h2>
        <div className={styles.filterWrap} ref={filterRef}>
          <button
            className={styles.filterGhost}
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            aria-expanded={filterOpen}
            aria-haspopup="listbox"
          >
            <CalendarDays size={15} />
            {dashboardPeriodHint}
          </button>
          {filterOpen && (
            <div className={styles.filterMenu} role="listbox" aria-label="Analytics timeframe">
              {ANALYTICS_TIMEFRAME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={analyticsTimeframe === option.value}
                  className={`${styles.filterOption} ${analyticsTimeframe === option.value ? styles.filterOptionActive : ""}`}
                  onClick={() => handleTimeframeChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {renderActiveTab()}
    </div>
  );
};

export default BuyAdminDashboard;
